import { useCallback, useEffect, useRef, useState } from 'react'
import { useInterval } from '@/hooks/useInterval'
import { useBefehlsnachrichtUpdater } from '@/features/befehlsnachrichten/hooks/useBefehlsnachrichtUpdater'
import { Abruftoken } from '@/models/Abruftoken'
import TokenExpiredError from '@/features/befehlsnachrichten/errors/TokenExpiredError'
import { useCaptureException } from '@repo/ui'

export interface BefehlsnachrichtUpdaterProps {
  abruftoken: Abruftoken
}

export default function BefehlsnachrichtUpdater({
  abruftoken
}: BefehlsnachrichtUpdaterProps) {
  const updateBefehlsnachricht = useBefehlsnachrichtUpdater()
  const [isRequestPending, setIsRequestPending] = useState(false)
  const hasPerformedInitialUpdate = useRef(false)
  const { captureException } = useCaptureException()

  const performUpdate = useCallback(() => {
    if (isRequestPending) {
      return
    }

    setIsRequestPending(true)

    updateBefehlsnachricht(abruftoken)
      .catch((err: unknown) => {
        if (err instanceof TokenExpiredError) {
          return
        }
        // The abruftoken, which cannot be fetched, is NOT included in the logging for security reasons.
        captureException(err, 'befehlsnachricht-updater', {})
      })
      .finally(() => {
        setIsRequestPending(false)
      })
  }, [captureException, isRequestPending, abruftoken, updateBefehlsnachricht])

  useEffect(() => {
    if (!hasPerformedInitialUpdate.current) {
      performUpdate()
      hasPerformedInitialUpdate.current = true
    }
  }, [performUpdate])

  useInterval(performUpdate, 3000)

  return null
}
